import { NON_CRYPTO_NETWORKS } from '../../constants/withdrawalWhitelist';
import { NetworkConfig, WhitelistEntry, WhitelistPage } from './types';

/**
 * The whitelist responses are camelCase on the entry fields but snake_case on
 * the express preference, and that mix is not settled with the backend yet.
 * Read every key both ways so a rename on either side cannot break the page.
 */
const readKey = <T>(source: any, ...keys: string[]): T | undefined => {
  if (!source) return undefined;
  const key = keys.find((candidate) => source[candidate] !== undefined);
  return key === undefined ? undefined : source[key];
};

const readString = (source: any, ...keys: string[]): string => {
  const value = readKey<unknown>(source, ...keys);
  // Addresses and memos are kept byte-for-byte: no trimming, no case change,
  // and "0" must survive as "0" rather than collapse to empty.
  return typeof value === 'string' ? value : '';
};

/** `total` comes back as a number here and as a string on neighbouring APIs. */
const readTotal = (source: any): number => {
  const value = readKey<unknown>(source, 'total');
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Wallet writes an absent rule as the four characters `NULL`, which would
 * otherwise compile into a pattern that matches the word and rejects every
 * real address.
 */
const readPattern = (value: string): string | null =>
  !value || value === 'NULL' ? null : value;

/**
 * Most networks carry `^$` — a rule that matches the empty string and nothing
 * else, which is how wallet says "this chain has no memo". Only a pattern that
 * admits some non-empty value means the field should be shown; 59 of the 72
 * live networks, TRON among them, do not.
 */
const supportsMemo = (pattern: string | null): boolean =>
  !!pattern && pattern.replace(/\s/g, '') !== '^$';

export const normalizeEntry = (raw: any): WhitelistEntry => ({
  id: readString(raw, 'id'),
  network: readString(raw, 'network'),
  address: readString(raw, 'address'),
  memo: readString(raw, 'memo'),
  label: readString(raw, 'label'),
  createdAt: readString(raw, 'createdAt', 'created_at', 'created'),
  updatedAt: readString(raw, 'updatedAt', 'updated_at', 'updated')
});

export const readExpressPreference = (data: any): boolean =>
  readKey<boolean>(
    data,
    'expressWithdrawalEnabled',
    'express_withdrawal_enabled'
  ) === true;

export const normalizeWhitelistPage = (response: any): WhitelistPage => {
  const data = response?.data ?? {};
  const entries = readKey<any[]>(data, 'entries', 'rows', 'items') ?? [];

  return {
    entries: Array.isArray(entries) ? entries.map(normalizeEntry) : [],
    expressWithdrawalEnabled: readExpressPreference(data),
    total: readTotal(data)
  };
};

/**
 * `/wallet/networks` returns one flat row per network. Map each into the shape
 * the add form is driven by, dropping the rails that are not crypto
 * destinations at all.
 */
export const normalizeNetworkCatalogue = (rows: any[]): NetworkConfig[] => {
  const byNetwork = new Map<string, NetworkConfig>();

  rows.forEach((network) => {
    const key = readString(network, 'symbol');
    if (!key || NON_CRYPTO_NETWORKS.includes(key)) return;
    if (byNetwork.has(key)) return;

    const memoPattern = readPattern(
      readString(network, 'memoRegex', 'memo_regex')
    );
    const translations = readKey<any[]>(network, 'translations');
    const nameIn = (language: string) =>
      readString(
        translations?.find((item) => item?.language === language),
        'name'
      );
    const rank = readKey<unknown>(network, 'rank');

    byNetwork.set(key, {
      key,
      displayName: nameIn('fa') || key,
      nameEn: nameIn('en'),
      tokenStandard: readString(network, 'tokenStandard', 'token_standard'),
      rank: typeof rank === 'number' && Number.isFinite(rank) ? rank : null,
      // A network wallet publishes no rule for is not one we can check an
      // address against, so anything typed for it is left to the backend.
      addressPattern: readPattern(
        readString(network, 'addressRegex', 'address_regex', 'walletRegex')
      ),
      memoPattern,
      memoSupported: supportsMemo(memoPattern),
      withdrawalEnabled:
        readKey<boolean>(
          network,
          'isWithdrawalActive',
          'is_withdrawal_active'
        ) !== false
    });
  });

  // The picker lists networks by wallet's rank, lowest first. A row with no
  // rank goes after the ranked ones and keeps the order wallet sent it in.
  return Array.from(byNetwork.values()).sort((a, b) => {
    if (a.rank === b.rank) return 0;
    if (a.rank === null) return 1;
    if (b.rank === null) return -1;
    return a.rank - b.rank;
  });
};
